import {
	useMemo,
	useRef,
	useEffect,
	useState,
	useCallback,
	MutableRefObject,
	MouseEvent,
	useId,
	useLayoutEffect,
} from "react"
import { ownerDocument, activeElement, contains, listen } from "./dom-helpers"

export function useDidUpdate(fn: () => void): void {
	const didMountRef = useRef(false)
	useEffect(() => {
		if (!didMountRef.current) {
			didMountRef.current = true
			return () => undefined
		}
		return fn()
	}, [fn])
}

export function useDidMount(fn: () => void): void {
	const ref = useRef(fn)
	useLayoutEffect(() => {
		ref.current = fn
	}, [fn])
	useEffect(() => ref.current(), [])
}

/**
 * useEnforceFocus
 * This hook lets us constrain the focus inside a container component based on a condition
 * and return back to last focused element when the condition got true
 *
 * @param element React.RefObject<HTMLElement> - Ref object (useRef) to container element
 * @param config { autoFocus: boolean, enforceFocus: boolean } - Should the container element be auto focused (autoFocus)
 * and should the last activeElement be focused back
 */
export function useEnforceFocus(
	element: React.RefObject<HTMLElement | null>,
	{
		init,
		autoFocus,
		restoreToLast,
		disabled,
	}: {
		init: boolean
		autoFocus: boolean
		restoreToLast: boolean
		disabled: boolean
	} = {
		init: true,
		autoFocus: true,
		restoreToLast: true,
		disabled: false,
	}
) {
	const lastActiveElementRef = useRef<HTMLElement | null>(activeElement())

	useLayoutEffect(() => {
		// store the last active element
		if (init) {
			lastActiveElementRef.current = activeElement()
		}
	}, [init])

	// focus the last focused element
	const focusOnLastActiveElement = useCallback(() => {
		if (!disabled && restoreToLast && lastActiveElementRef.current) {
			setTimeout(() => {
				lastActiveElementRef.current && lastActiveElementRef.current.focus()
			})
		}
	}, [restoreToLast, disabled])

	// focus back last element when closed
	useEffect(() => {
		if (!init && !disabled) {
			focusOnLastActiveElement()
		}
	}, [init, focusOnLastActiveElement, disabled])
	// or on unmount
	useDidMount(() => {
		return () => {
			if (!disabled && restoreToLast) {
				focusOnLastActiveElement()
			}
		}
	})

	// auto focus the container
	// focus the container when opening if autoFocus is set to true
	useEffect(() => {
		const currentActiveElement = activeElement()
		if (
			!disabled &&
			init &&
			autoFocus &&
			element.current &&
			!contains(element.current, currentActiveElement)
		) {
			element.current.focus()
		}
	}, [init, element, autoFocus, disabled])
	// and also enforce focus when tabbing through
	const handleFocusChange = useCallback(() => {
		if (!init || disabled) return
		const currentActiveElement = activeElement()
		if (element.current && !contains(element.current, currentActiveElement)) {
			element.current.focus()
		}
	}, [init, element, disabled])
	// add the event listeners
	useDidUpdate(
		useMemo(
			() => () => {
				const document = ownerDocument()
				if (init && document && !disabled) {
					return listen(document, "focusin" as never, handleFocusChange)
				}
				return
			},
			[init, handleFocusChange, disabled]
		)
	)
}

const escapeKeyCode = 27
const noop = () => undefined

function isLeftClickEvent(event: MouseEvent) {
	return event.button === 0
}

function isModifiedEvent(event: KeyboardEvent) {
	return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
}

export type RootCloseOptions = {
	disabled?: boolean
	clickTrigger?: keyof HTMLElementEventMap
}

const getRefTarget = (
	ref: React.RefObject<Element | null> | Element | null | undefined
) => ref && ("current" in ref ? ref.current : ref)

/**
 * This is used achieve modal style behavior where your
 * callback is triggered when the user tries to
 * interact with the rest of the document or hits the `esc` key.
 */
export function useRootClose(
	ref: React.RefObject<Element | null> | Element | null | undefined,
	onRootClose?: (e: Event) => void,
	{
		disabled,
		clickTrigger = "click",
		additionalRootCloseRefs = [],
	}: RootCloseOptions & {
		additionalRootCloseRefs?: (
			| React.RefObject<Element | null>
			| Element
			| null
			| undefined
		)[]
	} = {}
) {
	const preventMouseRootCloseRef = useRef(false)
	const onClose = onRootClose || noop

	const handleMouseCapture = useCallback(
		(e: MouseEvent) => {
			const currentTarget = getRefTarget(ref)
			const isInsideAdditionalRefs = additionalRootCloseRefs.some((r) => {
				const node = getRefTarget(r)
				return node && node.contains(e.target as Node)
			})
			if (!currentTarget) {
				console.warn(
					"RootClose captured a close event but does not have a ref to compare it to. " +
						"useRootClose(), should be passed a ref that resolves to a DOM node"
				)
			}

			preventMouseRootCloseRef.current =
				!currentTarget ||
				isModifiedEvent(e as never) ||
				!isLeftClickEvent(e as never) ||
				!!contains(currentTarget, e.target as never) ||
				isInsideAdditionalRefs
		},
		[ref, additionalRootCloseRefs]
	)

	const handleMouse = useCallback(
		(e: Event) => {
			if (!preventMouseRootCloseRef.current) {
				onClose(e)
			}
		},
		[onClose]
	)

	const handleKeyUp = useCallback(
		(e: KeyboardEvent) => {
			if (e.keyCode === escapeKeyCode) {
				onClose(e)
			}
		},
		[onClose]
	)

	useEffect(() => {
		const document = ownerDocument()
		if (disabled || ref == null || !document) return undefined

		// Store the current event to avoid triggering handlers immediately
		// https://github.com/facebook/react/issues/20074
		let currentEvent = window.event

		// Use capture for this listener so it fires before React's listener, to
		// avoid false positives in the contains() check below if the target DOM
		// element is removed in the React mouse callback.
		const removeMouseCaptureListener = listen(
			document,
			clickTrigger,
			handleMouseCapture as never,
			true
		)

		const removeMouseListener = listen(document, clickTrigger, (e) => {
			// skip if this event is the same as the one running when we added the handlers
			if (e === currentEvent) {
				currentEvent = undefined
				return
			}
			handleMouse(e)
		})
		const removeKeyupListener = listen(document, "keyup", (e) => {
			// skip if this event is the same as the one running when we added the handlers
			if (e === currentEvent) {
				currentEvent = undefined
				return
			}
			handleKeyUp(e)
		})

		let mobileSafariHackListeners: Array<() => unknown> = []
		if ("ontouchstart" in document.documentElement) {
			mobileSafariHackListeners = [].slice
				.call(document.body.children)
				.map((el: HTMLElement) => listen(el, "mousemove", noop))
		}

		return () => {
			removeMouseCaptureListener()
			removeMouseListener()
			removeKeyupListener()
			mobileSafariHackListeners.forEach((remove) => remove())
		}
	}, [
		ref,
		disabled,
		clickTrigger,
		handleMouseCapture,
		handleMouse,
		handleKeyUp,
	])
}

export { useId }

/**
 * Hook to use the ref using `useState` designed to work with callback refs
 *
 * Callback refs are useful over `useRef()` when you need to respond to the ref being set
 * instead of lazily accessing it in an effect.
 */
export function useCallbackRef<TValue = unknown>(): [
	TValue | null,
	(ref: TValue | null) => void,
] {
	return useState<TValue | null>(null)
}

export type CallbackRef<T> = (ref: T | null) => void

export type Ref<T> = MutableRefObject<T> | CallbackRef<T>

function toFnRef<T>(ref?: Ref<T> | null): undefined | CallbackRef<T> {
	return (
		!ref || typeof ref === "function"
			? ref || undefined
			: (value: T) => {
					ref.current = value
				}
	) as CallbackRef<T> | undefined
}

export function mergeRefs<T>(
	refA?: Ref<T> | null | undefined,
	refB?: Ref<T> | null | undefined
) {
	const a = toFnRef(refA)
	const b = toFnRef(refB)
	return (value: T | null) => {
		if (a) a(value)
		if (b) b(value)
	}
}

/**
 * Create and returns a single callback ref composed from two other Refs.
 *
 * Usefull when setting multiple refs with a single call
 */
export function useMergedRefs<T>(
	refA?: Ref<T> | null | undefined,
	refB?: Ref<T> | null | undefined
) {
	return useMemo(() => mergeRefs(refA, refB), [refA, refB])
}
export type DOMContainer<T extends HTMLElement = HTMLElement> =
	| T
	| React.RefObject<T | null>
	| null
	| (() => T | React.RefObject<T | null> | null)

export function resolveContainerRef<T extends HTMLElement>(
	ref: DOMContainer<T> | undefined
): T | HTMLBodyElement | null {
	if (typeof document === "undefined") return null
	if (ref == null) {
		const doc = ownerDocument()
		if (!doc) return null
		return doc.body as HTMLBodyElement
	}
	if (typeof ref === "function") ref = ref()

	if (ref && "current" in ref) ref = ref.current
	if ((ref as never as { nodeType: unknown })?.nodeType)
		return (ref as unknown as T) || null

	return null
}

export function useWaitForDOMRef<T extends HTMLElement = HTMLElement>(
	ref: DOMContainer<T> | undefined,
	onResolved?: (element: T | HTMLBodyElement) => void
) {
	const [resolvedRef, setRef] = useState(() => resolveContainerRef(ref))

	if (!resolvedRef) {
		const earlyRef = resolveContainerRef(ref)
		if (earlyRef) setRef(earlyRef)
	}

	useEffect(() => {
		if (onResolved && resolvedRef) {
			onResolved(resolvedRef)
		}
	}, [onResolved, resolvedRef])

	useEffect(() => {
		const nextRef = resolveContainerRef(ref)
		if (nextRef !== resolvedRef) {
			const h = setTimeout(() => {
				setRef(nextRef)
			})
			return () => clearTimeout(h)
		}
		return () => undefined
	}, [ref, resolvedRef])

	return resolvedRef
}

export function useMounted(): () => boolean {
	const mountedRef = useRef<boolean>(false)
	const get = useCallback(() => mountedRef.current, [])
	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])
	return get
}

/**
 * Returns a ref that is immediately updated with the new value
 *
 * The returned "ref" won't change on re-rerenders, only the ref.current
 * will be updated
 *
 * @param value The Ref value
 */
export function useUpdatedRef<T>(value: T) {
	const valueRef = useRef<T>(value)
	useLayoutEffect(() => {
		valueRef.current = value
	}, [value])
	return valueRef
}

/**
 * Attach a callback that fires when a component unmounts
 *
 * @param fn Handler to run when the component unmounts
 */
export function useWillUnmount(fn: () => void) {
	const onUnmount = useUpdatedRef(fn)
	useEffect(() => () => onUnmount.current(), [onUnmount])
}

/**
 * Returns a controller object for setting a timeout that is properly cleaned up
 * once the component unmounts. New timeouts cancel and replace existing ones.
 */
export function useTimeout() {
	const isMounted = useMounted()
	const handleRef = useRef<NodeJS.Timeout | undefined>(undefined)
	useWillUnmount(() => handleRef.current && clearTimeout(handleRef.current))
	return useMemo(() => {
		const clear = () => handleRef.current && clearTimeout(handleRef.current)
		function set(fn: () => void, delayMs = 0): void {
			if (!isMounted()) return
			clear()
			if (delayMs <= MAX_DELAY_MS) {
				// For simplicity, if the timeout is short, just set a normal timeout.
				handleRef.current = setTimeout(fn, delayMs)
			} else {
				setChainedTimeout(handleRef, fn, Date.now() + delayMs)
			}
		}
		return {
			set,
			clear,
		}
	}, [isMounted])
}

/*
 * Browsers including Internet Explorer, Chrome, Safari, and Firefox store the
 * delay as a 32-bit signed integer internally. This causes an integer overflow
 * when using delays larger than 2,147,483,647 ms (about 24.8 days),
 * resulting in the timeout being executed immediately.
 *
 * via: https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout
 */
const MAX_DELAY_MS = 2 ** 31 - 1

function setChainedTimeout(
	handleRef: MutableRefObject<NodeJS.Timeout | undefined>,
	fn: () => void,
	timeoutAtMs: number
) {
	const delayMs = timeoutAtMs - Date.now()

	handleRef.current =
		delayMs <= MAX_DELAY_MS
			? setTimeout(fn, delayMs)
			: setTimeout(
					() => setChainedTimeout(handleRef, fn, timeoutAtMs),
					MAX_DELAY_MS
				)
}

/**
 * Whether a file is one an `accept` attribute allows: a MIME type
 * (`image/png`), a MIME family (`image/*`) or an extension (`.pdf`). A missing
 * or empty `accept` allows any file.
 */
export function fileMatchesAccept(file: File, accept?: string): boolean {
	const tokens = (accept ?? "")
		.split(",")
		.map((token) => token.trim().toLowerCase())
		.filter(Boolean)

	if (!tokens.length) return true

	const type = file.type.toLowerCase()
	const name = file.name.toLowerCase()

	return tokens.some((token) => {
		if (token.startsWith(".")) return name.endsWith(token)
		if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1))
		return type === token
	})
}

/**
 * Drops already taken by a drop area nested inside another, so the outer one
 * only resets its own state. The innermost area sees a drop first.
 */
const handledDrops = new WeakSet<Event>()

function carriesFiles(event: { dataTransfer: DataTransfer | null }): boolean {
	return Array.from(event.dataTransfer?.types ?? []).includes("Files")
}

/**
 * Takes files pasted into an element, a screenshot say. Spread
 * `pasteHandlers` on it.
 *
 * A paste that carries text is left to the text box: copied cells or a web
 * page bring a picture of themselves along, but the text is what was meant.
 * `onPaste` gets the pasted files and the ones `accept` does not allow. Where
 * paste areas nest, the innermost one takes the paste.
 */
export function usePastedFiles(
	onPaste: (files: FileList, rejected: Array<File>) => void,
	accept?: string,
	isDisabled = false
) {
	const pasteHandlers = {
		onPaste(event: React.ClipboardEvent) {
			const { clipboardData } = event
			if (
				isDisabled ||
				// Taken already by a paste area nested inside. The native event's
				// flag, as React's copy is not updated as the event bubbles.
				event.nativeEvent.defaultPrevented ||
				!clipboardData.files.length ||
				clipboardData.types.includes("text/plain")
			) {
				return
			}
			event.preventDefault()

			onPaste(
				clipboardData.files,
				Array.from(clipboardData.files).filter(
					(file) => !fileMatchesAccept(file, accept)
				)
			)
		},
	}

	return { pasteHandlers }
}

/**
 * How long after the last `dragover` a file drag counts as over. Browsers
 * repeat `dragover` every few hundred milliseconds while a drag is over the
 * page, even with the pointer still.
 */
const DOCUMENT_FILE_DRAG_IDLE_MS = 1000

/**
 * Whether files are being dragged anywhere over the page, so drop areas can
 * show themselves before the pointer reaches them.
 *
 * A drop outside any drop area fires no `drop` or `dragleave` here: the
 * browser opens the file instead. So the drag counts as over once `dragover`
 * stops coming, however it ended, and at once on a drop the page sees.
 */
export function useDocumentFileDrag(isDisabled = false): boolean {
	const [isDragging, setIsDragging] = useState(false)

	useEffect(() => {
		if (isDisabled) return
		let idleTimeout: ReturnType<typeof setTimeout> | undefined

		function end() {
			clearTimeout(idleTimeout)
			setIsDragging(false)
		}
		function onDrag(event: DragEvent) {
			if (!carriesFiles(event)) return
			setIsDragging(true)
			clearTimeout(idleTimeout)
			idleTimeout = setTimeout(end, DOCUMENT_FILE_DRAG_IDLE_MS)
		}

		document.addEventListener("dragenter", onDrag)
		document.addEventListener("dragover", onDrag)
		document.addEventListener("drop", end)
		document.addEventListener("dragend", end)

		return () => {
			clearTimeout(idleTimeout)
			document.removeEventListener("dragenter", onDrag)
			document.removeEventListener("dragover", onDrag)
			document.removeEventListener("drop", end)
			document.removeEventListener("dragend", end)
		}
	}, [isDisabled])

	return isDragging && !isDisabled
}

/**
 * Makes an element a drop area for files. Spread `dropHandlers` on it and use
 * `isDragging` to show that a drop is welcome.
 *
 * Only a drag carrying files is taken; dragged text or links pass through.
 * `onDrop` gets every dropped file and the ones `accept` does not allow, since
 * a browser's `accept` filters its file picker but not a drop. Where drop
 * areas nest, the innermost one takes the drop.
 */
export function useFileDrop(
	onDrop: (files: FileList, rejected: Array<File>) => void,
	accept?: string,
	isDisabled = false
) {
	const [isDragging, setIsDragging] = useState(false)
	// Entering a child fires dragenter on it before dragleave on the parent,
	// so the area counts nested enters rather than trusting the last event.
	const depth = useRef(0)

	function reset() {
		depth.current = 0
		setIsDragging(false)
	}

	const dropHandlers = {
		onDragEnter(event: React.DragEvent) {
			if (isDisabled || !carriesFiles(event)) return
			event.preventDefault()
			depth.current += 1
			setIsDragging(true)
		},
		onDragOver(event: React.DragEvent) {
			if (isDisabled || !carriesFiles(event)) return
			// Without this the browser opens the file instead of dropping it.
			event.preventDefault()
			event.dataTransfer.dropEffect = "copy"
		},
		onDragLeave(event: React.DragEvent) {
			if (isDisabled || !carriesFiles(event)) return
			depth.current = Math.max(0, depth.current - 1)
			if (depth.current === 0) setIsDragging(false)
		},
		onDrop(event: React.DragEvent) {
			if (isDisabled || !carriesFiles(event)) return
			event.preventDefault()
			reset()

			if (handledDrops.has(event.nativeEvent)) return
			handledDrops.add(event.nativeEvent)

			const files = event.dataTransfer.files
			if (!files.length) return

			onDrop(
				files,
				Array.from(files).filter((file) => !fileMatchesAccept(file, accept))
			)
		},
	}

	return { isDragging, dropHandlers }
}
